import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

/* A raw id says nothing to a client ("Hire Requests › 239"), so detail routes
   get a name. Ordered like adminReact's DYNAMIC_TITLES — first match wins, so
   static siblings sit above the catch-all id patterns that would swallow them. */
const CRUMB_TITLES = [
  [/^\/project\/ongoing-project$/, 'Ongoing Project'],
  [/^\/project\/offer$/, 'Offers'],
  [/^\/hire-requests\/[^/]+$/, 'Request Details'],
  [/^\/invites\/[^/]+$/, 'Invite Details'],
  [/^\/project\/[^/]+$/, 'Project Details'],
];

const labelFor = (path, segment) => {
  const match = CRUMB_TITLES.find(([re]) => re.test(path));
  return match ? match[1] : segment.replace(/-/g, ' ');
};

const Breadcrumb = () => {
  const { pathname } = useLocation();
  const parts = pathname.split('/').filter((part) => part !== '');

  if (pathname.startsWith('/team/')) {
    return null;
  }

  if (parts.length <= 1) {
    return null;
  }

  const crumbs = parts.slice(0, 2).map((segment, index) => {
    const path = `/${parts.slice(0, index + 1).join('/')}`;
    return { path, label: labelFor(path, segment) };
  });

  return (
    <nav
      aria-label='Breadcrumb'
      className='mb-4 flex flex-wrap items-center gap-1.5 text-xs font-medium text-muted-foreground'
    >
      {crumbs.map(({ path, label }, index) => {
        /* Not "is it last" — on a deeper route the trail is truncated, so the
           final crumb rendered is still a real destination. */
        const isCurrent = path === pathname;

        return (
          <span key={path} className='flex items-center gap-1.5'>
            {index > 0 && <ChevronRight className='h-3 w-3 shrink-0' />}
            {isCurrent ? (
              <span aria-current='page' className='capitalize text-brand-navy'>
                {label}
              </span>
            ) : (
              <Link
                to={path}
                className='capitalize transition-colors hover:text-brand-orange'
              >
                {label}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
};

export default Breadcrumb;
